import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, FormControl, Select, MenuItem, Button, CircularProgress, Alert, Collapse, IconButton, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LabeledTextField from "./LabeledTextField";
import LargeTextField from "./LargeTextField";
import TextFieldWithSelector from "./TextFieldWithSelector";
import ManagementQualityTable from "./ManagementQualityTable";
import SectionHeader from "./SectionHeader";
import getAPI from "@API/index";

const RISK_OPTIONS = ["низкие", "приемл", "повыш", "высокие"];

const CompanyTab = ({ companyData, companyIndex, onUpdate, clientId, onDataRefresh, activityTypes = [], onSave }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const pollIntervalRef = useRef(null);

  // Build industries and activity types map from API data
  const { industries, activityTypesMap } = useMemo(() => {
    const industriesSet = new Set();
    const typesMap = {};

    activityTypes.forEach(item => {
      if (item.industry) {
        industriesSet.add(item.industry);

        if (!typesMap[item.industry]) {
          typesMap[item.industry] = [];
        }

        if (item.activity_type) {
          typesMap[item.industry].push(item.activity_type);
        }
      }
    });

    return {
      industries: Array.from(industriesSet).sort(),
      activityTypesMap: typesMap
    };
  }, [activityTypes]);

  useEffect(() => {
    const status = companyData?.aiAnalysisStatus;

    // Check if analysis is in progress when component mounts or data changes
    if (status === "pending") {
      if (!isAnalyzing) {
        setIsAnalyzing(true);
      }
      if (!pollIntervalRef.current) {
        startPolling();
      }
    } else {
      if (isAnalyzing) {
        setIsAnalyzing(false);
      }
      // Stop polling when analysis is completed or failed
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    if (status === "failed") {
      setAnalysisError(companyData.aiAnalysisError);
    } else if (status !== "pending") {
      setAnalysisError(null);
    }
  }, [companyData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const startPolling = () => {
    // Clear any existing interval first
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const intervalId = setInterval(async () => {
      if (onDataRefresh) {
        await onDataRefresh();
      }
    }, 5000); // Poll every 5 seconds

    pollIntervalRef.current = intervalId;
  };

  const handleAIAnalysis = async () => {
    if (!companyData?.inn || !companyData?.activityType) {
      setAnalysisError("ИНН и вид деятельности должны быть заполнены");
      return;
    }

    try {
      setIsAnalyzing(true);
      setAnalysisError(null);

      // Save data before starting AI analysis
      if (onSave) {
        await onSave();
      }

      await getAPI().request({
        url: `/api/sdm/client/${clientId}/credit-opinion/company/${companyIndex}/ai-analysis`,
        method: "post"
      });

      // Start polling for updates
      startPolling();

      // Refresh data immediately to show pending status
      if (onDataRefresh) {
        await onDataRefresh();
      }
    } catch (error) {
      console.error("Error starting AI analysis:", error);
      setAnalysisError(error.response?.data?.error || "Ошибка запуска AI анализа");
      setIsAnalyzing(false);
    }
  };

  const updateField = (field, value) => {
    onUpdate(`generalInfo.companiesInfo.${companyIndex}.${field}`, value);
  };

  const updateAnalysisField = (field, subfield, value) => {
    onUpdate(`generalInfo.companiesInfo.${companyIndex}.${field}.${subfield}`, value);
  };

  const isAiFieldsDisabled = isAnalyzing || companyData?.aiAnalysisStatus === "pending";

  return (
    <Box>
      {/* Section: Qualitative analysis */}
      <Box sx={{ marginBottom: "32px" }}>
        <SectionHeader>Качественный анализ заемщика</SectionHeader>

        {/* Industry and Activity Type selectors */}
        <Box sx={{
          display: "flex",
          gap: "16px",
          marginBottom: "24px"
        }}>
          <FormControl fullWidth size="small">
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Отрасль
            </Box>
            <Select
              value={companyData?.industry || ""}
              onChange={(e) => {
                updateField("industry", e.target.value);
                updateField("activityType", "");
              }}
              displayEmpty
              disabled={isAiFieldsDisabled}
            >
              <MenuItem value="">
                <em>Выберите отрасль</em>
              </MenuItem>
              {industries.map((industry, index) => (
                <MenuItem key={index} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <Box sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#1A1E24",
              marginBottom: "8px"
            }}>
              Вид деятельности
            </Box>
            <Autocomplete
              freeSolo
              size="small"
              disabled={!companyData?.industry || isAiFieldsDisabled}
              options={(companyData?.industry && activityTypesMap[companyData.industry]) || []}
              value={companyData?.activityType || ""}
              inputValue={companyData?.activityType || ""}
              onChange={(e, newValue) => {
                const opts = (companyData?.industry && activityTypesMap[companyData.industry]) || [];
                const isCustomActivity = !!newValue && !opts.includes(newValue);
                updateField("activityType", newValue || "");
                updateField("customActivityType", isCustomActivity);
              }}
              onInputChange={(e, newValue, reason) => {
                if (reason === "input") {
                  const opts = (companyData?.industry && activityTypesMap[companyData.industry]) || [];
                  const isCustomActivity = !!newValue && !opts.includes(newValue);
                  updateField("activityType", newValue);
                  updateField("customActivityType", isCustomActivity);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Выберите или введите вид деятельности" />
              )}
            />
          </FormControl>
        </Box>

        {/* AI Analysis Button */}
        <Box sx={{ marginBottom: "24px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Button
              variant="contained"
              startIcon={isAiFieldsDisabled ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleAIAnalysis}
              disabled={isAiFieldsDisabled || !companyData?.inn || !companyData?.activityType}
              sx={{
                backgroundColor: "#6B4CE6",
                "&:hover": {
                  backgroundColor: "#5A3DD4"
                },
                textTransform: "none",
                fontWeight: 500
              }}
            >
              {isAiFieldsDisabled ? "AI анализ выполняется..." : "Запросить AI анализ"}
            </Button>

            {(analysisError || companyData?.aiAnalysisStatus === "completed") && (
              <Alert
                severity={analysisError ? "error" : "success"}
                sx={{
                  margin: 0,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  padding: "0px 16px",
                  minHeight: "36.5px"
                }}
              >
                {analysisError || "AI анализ успешно завершен"}
              </Alert>
            )}
          </Box>

          {companyData?.aiAnalysisStatus === "completed" && companyData?.aiAnalysisSources && (
            <Box sx={{ marginTop: "12px", border: "1px solid #E0E0E0", borderRadius: "8px", overflow: "hidden" }}>
              <Box
                onClick={() => setSourcesExpanded(!sourcesExpanded)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0px 16px",
                  cursor: "pointer",
                  userSelect: "none",
                  backgroundColor: "#F0EBFF",
                  "&:hover": {
                    backgroundColor: "#dcd0ffff"
                  }
                }}
              >
                <Box sx={{ fontWeight: 500, fontSize: "14px" }}>Источники информации анализа</Box>
                <IconButton
                  size="small"
                  sx={{
                    transform: sourcesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s"
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Box>
              <Collapse in={sourcesExpanded}>
                <Box sx={{ padding: "16px", backgroundColor: "#FAFAFA", fontSize: "14px", whiteSpace: "pre-wrap" }}>
                  {companyData.aiAnalysisSources}
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>

        {/* AI Fields with loader overlay */}
        <Box sx={{ position: "relative" }}>
          {isAiFieldsDisabled && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: "8px"
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={40} />
                <Box sx={{ marginTop: "16px", fontSize: "14px", color: "#666" }}>
                  AI анализирует данные компании...
                </Box>
              </Box>
            </Box>
          )}

          <LargeTextField
            label="СУТЬ БИЗНЕСА КЛИЕНТА:"
            value={companyData?.businessEssence || ""}
            onChange={(val) => updateField("businessEssence", val)}
            disabled={isAiFieldsDisabled}
            backgroundColor="#F0EBFF"
          />

          <TextFieldWithSelector
            label="Положение отрасли:"
            textValue={companyData?.industryCondition?.value || ""}
            selectorValue={companyData?.industryCondition?.selector || ""}
            onTextChange={(val) => updateAnalysisField("industryCondition", "value", val)}
            onSelectorChange={(val) => updateAnalysisField("industryCondition", "selector", val)}
            disabled={isAiFieldsDisabled}
            backgroundColor="#F0EBFF"
          />

          <TextFieldWithSelector
            label="Положение клиента в отрасли:"
            textValue={companyData?.clientPositionInIndustry?.value || ""}
            selectorValue={companyData?.clientPositionInIndustry?.selector || ""}
            onTextChange={(val) => updateAnalysisField("clientPositionInIndustry", "value", val)}
            onSelectorChange={(val) => updateAnalysisField("clientPositionInIndustry", "selector", val)}
            disabled={isAiFieldsDisabled}
            backgroundColor="#F0EBFF"
          />
        </Box>

        <TextFieldWithSelector
          label="Прозрачность бизнеса:"
          textValue={companyData?.businessTransparency?.value || ""}
          selectorValue={companyData?.businessTransparency?.selector || ""}
          onTextChange={(val) => updateAnalysisField("businessTransparency", "value", val)}
          onSelectorChange={(val) => updateAnalysisField("businessTransparency", "selector", val)}
        />

        <TextFieldWithSelector
          label="Репутация клиента, рекомендации:"
          textValue={companyData?.clientReputationAndReferences?.value || ""}
          selectorValue={companyData?.clientReputationAndReferences?.selector || ""}
          onTextChange={(val) => updateAnalysisField("clientReputationAndReferences", "value", val)}
          onSelectorChange={(val) => updateAnalysisField("clientReputationAndReferences", "selector", val)}
        />

        <ManagementQualityTable
          data={companyData?.managementQuality || { rows: [], selector: "" }}
          onChange={(val) => updateField("managementQuality", val)}
        />

        <TextFieldWithSelector
          label="Общее количество постоянных поставщиков и покупателей, условия расчетов с ними:"
          textValue={companyData?.counterparties?.value || ""}
          selectorValue={companyData?.counterparties?.selector || ""}
          onTextChange={(val) => updateAnalysisField("counterparties", "value", val)}
          onSelectorChange={(val) => updateAnalysisField("counterparties", "selector", val)}
        />

        <TextFieldWithSelector
          label="Качество залога и его достаточность:"
          textValue={companyData?.collateralQualityAndSufficiency?.value || ""}
          selectorValue={companyData?.collateralQualityAndSufficiency?.selector || ""}
          onTextChange={(val) => updateAnalysisField("collateralQualityAndSufficiency", "value", val)}
          onSelectorChange={(val) => updateAnalysisField("collateralQualityAndSufficiency", "selector", val)}
        />

        <TextFieldWithSelector
          label="Риски бизнеса клиента:"
          textValue={companyData?.businessRisksOfClient?.value || ""}
          selectorValue={companyData?.businessRisksOfClient?.selector || ""}
          onTextChange={(val) => updateAnalysisField("businessRisksOfClient", "value", val)}
          onSelectorChange={(val) => updateAnalysisField("businessRisksOfClient", "selector", val)}
          options={RISK_OPTIONS}
        />

        <TextFieldWithSelector
          label="Риски кредитной сделки:"
          textValue={companyData?.risksOfCreditTransaction?.value || ""}
          selectorValue={companyData?.risksOfCreditTransaction?.selector || ""}
          onTextChange={(val) => updateAnalysisField("risksOfCreditTransaction", "value", val)}
          onSelectorChange={(val) => updateAnalysisField("risksOfCreditTransaction", "selector", val)}
          options={RISK_OPTIONS}
        />
      </Box>

      {/* Section: General summary */}
      <Box sx={{ marginBottom: "32px" }}>
        <SectionHeader>Заключение</SectionHeader>

        <LargeTextField
          label="Общий вывод по компании:"
          value={companyData?.generalSummary || ""}
          onChange={(val) => updateField("generalSummary", val)}
          rows={4}
        />
      </Box>
    </Box>
  );
};

export default CompanyTab;
